/**
 * SPROUTIES PRODUCT PAGE — Combined JavaScript
 * Phase 4 Development · February 2026
 *
 * File: sprouties-product.js
 * Load: defer
 * Scope: All sprouties-* sections and snippets
 *
 * Modules:
 * 1. SproutiesSubscription — Widget logic for both Banner and Alt variants
 * 2. SproutiesGallery — Mobile swipe + desktop thumbnail nav
 * 3. SproutiesStickyCTA — Mobile sticky bottom bar (IntersectionObserver)
 * 4. SproutiesCountdown — Shipping cutoff countdown (8:30am UAE / GMT+4)
 * 5. SproutiesFAQ — Single-open accordion
 * 6. SproutiesReveal — Section scroll-reveal animations
 * 7. SproutiesTicker — Review ticker pause on touch
 */

(function () {
  'use strict';

  /* ========================================================================
     CONSTANTS
     ======================================================================== */

  const DELIVERY_SAVING = 10; // AED saved per delivery vs one-time shipping
  const UAE_OFFSET_HOURS = 4; // GMT+4
  const DEFAULT_CUTOFF_HOUR = 8;  // 8:30am UAE time
  const DEFAULT_CUTOFF_MIN = 30;

  /**
   * Shared delivery-date helper.
   * Single source of truth for "when is the next delivery?" logic.
   * Both SproutiesCountdown and SproutiesSubscription consume this.
   *
   * Rules:
   * - Deliver every day except Sunday.
   * - Before 8:30am UAE (Mon-Sat): countdown active, delivery = today.
   * - After 8:30am UAE (Mon-Fri): static, delivery = tomorrow.
   * - After 8:30am UAE (Saturday): static, delivery = Monday.
   * - All day Sunday: static, delivery = Monday.
   *
   * @returns {{ deliveryDate: Date, uaeNow: Date, beforeCutoff: boolean, remainingMin: number, isToday: boolean }}
   */
  function getNextDeliveryInfo() {
    const now = new Date();
    // UTC-safe: convert local -> UAE regardless of browser timezone
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const uaeMs = utcMs + UAE_OFFSET_HOURS * 3600000;
    const uaeNow = new Date(uaeMs);

    const currentHour = uaeNow.getHours();
    const currentMin = uaeNow.getMinutes();
    const currentTotalMin = currentHour * 60 + currentMin;
    const cutoffTotalMin = DEFAULT_CUTOFF_HOUR * 60 + DEFAULT_CUTOFF_MIN;
    const dayOfWeek = uaeNow.getDay(); // 0=Sun, 6=Sat

    const isSunday = dayOfWeek === 0;
    const isSaturday = dayOfWeek === 6;
    const isBeforeCutoff = currentTotalMin < cutoffTotalMin;

    let deliveryDate = new Date(uaeNow);
    let beforeCutoff = false;
    let isToday = false;

    if (isSunday) {
      // Sunday: no delivery today, next is Monday
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else if (isBeforeCutoff) {
      // Mon-Sat before 8:30am: delivery is today, countdown active
      beforeCutoff = true;
      isToday = true;
      // deliveryDate stays as today
    } else if (isSaturday) {
      // Saturday after 8:30am: next delivery is Monday (+2)
      deliveryDate.setDate(deliveryDate.getDate() + 2);
    } else {
      // Mon-Fri after 8:30am: next delivery is tomorrow
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      // If tomorrow is Sunday, skip to Monday
      if (deliveryDate.getDay() === 0) {
        deliveryDate.setDate(deliveryDate.getDate() + 1);
      }
    }

    // Minutes remaining until cutoff (only meaningful when beforeCutoff is true)
    const remainingMin = beforeCutoff ? cutoffTotalMin - currentTotalMin : 0;

    return { deliveryDate, uaeNow, beforeCutoff, remainingMin, isToday };
  }

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Pricing data (matches Phase 3 spec exactly)
  // These are fallbacks; actual prices come from Shopify selling plan allocations
  const PLAN_META = {
    biweekly: {
      boxesPerMonth: 2, deliveriesPerMonth: 2,
      bannerLabel: 'Biweekly', altLabel: 'Every 2 weeks',
      bannerBadge: 'Most Popular', altBadge: 'Most Popular',
      boxesSuffix: '~2 deliveries/month'
    },
    '10day': {
      boxesPerMonth: 3, deliveriesPerMonth: 3,
      bannerLabel: '3x Rotation', altLabel: 'Every 10 days',
      bannerBadge: null, altBadge: null,
      boxesSuffix: '~3 deliveries/month'
    },
    weekly: {
      boxesPerMonth: 4, deliveriesPerMonth: 4,
      bannerLabel: 'Weekly', altLabel: 'Every week',
      bannerBadge: null, altBadge: null,
      boxesSuffix: '~4 deliveries/month'
    }
  };

  /* ========================================================================
     1. SUBSCRIPTION WIDGET
     Handles both Banner and Alt (Collapse) variants.
     Reads plan data from the JSON blob rendered by sprouties-purchase-option-toggle.
     Syncs selling_plan hidden input with the product form.
     ======================================================================== */

  class SproutiesSubscription {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.sectionId = this.container.dataset.sectionId;
      this.variant = this.container.dataset.widgetVariant || 'banner'; // 'banner' or 'alt'

      // Read plan data from JSON blob
      const dataEl = document.getElementById(`sproutiesPlansData_${this.sectionId}`);
      if (!dataEl) return;

      try {
        this.plansData = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('Sprouties: Failed to parse plan data', e);
        return;
      }

      this.oneTimePrice = this.plansData.oneTimePrice;
      this.plans = this.plansData.plans || [];
      this.sellingPlanInput = document.getElementById(`sproutiesSellingPlan_${this.sectionId}`);

      // Read customizer settings from data attributes (fallbacks = safe defaults)
      this.deliverySavingAed = parseFloat(this.container.dataset.deliverySaving) || DELIVERY_SAVING;
      this.includeDeliverySavings = this.container.dataset.includeDeliverySavings !== 'false';
      this.subscriberCount = this.container.dataset.subscriberCount || '4,500';
      this.subscribeCtaOverride = (this.container.dataset.subscribeCtaOverride || '').trim();
      this.popularPlanTarget = this.plansData.popularPlanTarget || 'biweekly';
      this.bundleMode = !!this.plansData.bundleMode;

      // State
      this.purchaseType = this.plansData.defaultSelection || 'subscribe';
      this.selectedPlanIndex = this._getDefaultPlanIndex();

      this._cacheElements();
      this._bindEvents();
      this._renderFrequencyLabels();
      this._updateUI();

      // Set initial expandable height for CSS transitions
      if (this.subscribeExpandable && this.purchaseType === 'subscribe') {
        this.subscribeExpandable.style.maxHeight = this.subscribeExpandable.scrollHeight + 'px';
        // Clear after paint so CSS handles future transitions naturally
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.subscribeExpandable) {
              this.subscribeExpandable.style.maxHeight = '';
            }
          });
        });
      }
    }

    _getDefaultPlanIndex() {
      // Find the plan marked as popular (biweekly), or fall back to first
      const popular = this.plansData.popularPlanIndex;
      if (typeof popular === 'number' && popular >= 0 && popular < this.plans.length) {
        return popular;
      }
      return 0;
    }

    _cacheElements() {
      // Purchase type radios
      this.subscribeRadio = this.container.querySelector('[data-purchase-type="subscribe"]');
      this.onetimeRadio = this.container.querySelector('[data-purchase-type="onetime"]');

      // Frequency selector
      this.frequencyContainer = this.container.querySelector('[data-frequency-selector]');
      this.frequencyRadios = this.container.querySelectorAll('[data-plan-index]');

      // Price displays
      this.priceDisplay = this.container.querySelector('[data-price-display]');
      this.comparePriceDisplay = this.container.querySelector('[data-compare-price]');

      // CTA button
      this.ctaButton = this.container.querySelector('[data-sprouties-cta]');
      this.ctaLabel = this.container.querySelector('[data-cta-label]');

      // Alt variant specific
      this.summaryLine = this.container.querySelector('[data-alt-summary]');
      this.summaryMonthly = this.container.querySelector('[data-alt-monthly]');
      this.summaryDelivery = this.container.querySelector('[data-alt-delivery]');
      this.breakdownToggle = this.container.querySelector('[data-breakdown-toggle]');
      this.breakdownPanel = this.container.querySelector('[data-breakdown-panel]');
      this.breakdownChevron = this.container.querySelector('[data-breakdown-chevron]');

      // Breakdown detail elements (Alt)
      this.breakdownBoxSavings = this.container.querySelector('[data-bd-box-savings]');
      this.breakdownDeliverySavings = this.container.querySelector('[data-bd-delivery-savings]');
      this.breakdownTotal = this.container.querySelector('[data-bd-total]');

      // Subscribe nudge (shown when Buy Once selected)
      this.subscribeNudge = this.container.querySelector('[data-subscribe-nudge]');

      // New nested structure elements
      this.subscribeExpandable = this.container.querySelector('[data-subscribe-expandable]');
      this.bannerValue = this.container.querySelector('[data-banner-value]');
      this.bannerSavingsAmount = this.container.querySelector('[data-banner-savings-amount]');
      this.bannerStrip = this.container.querySelector('[data-banner-strip]');
      this.bannerStripText = this.container.querySelector('[data-banner-strip-text]');
      this.savingsSubtitle = this.container.querySelector('[data-savings-subtitle]');
      this.monthlyInfo = this.container.querySelector('[data-monthly-info]');
      this.subscriberLine = this.container.querySelector('[data-subscriber-line]');

      // Frequency pill labels and badges
      this.frequencyLabels = this.container.querySelectorAll('[data-freq-label]');
      this.frequencyBadges = this.container.querySelectorAll('[data-freq-badge]');

      // Sticky CTA (external, found in page)
      this.stickyCta = document.querySelector('[data-sprouties-sticky-cta]');
      this.stickyCtaPrice = this.stickyCta?.querySelector('[data-sticky-price]');
      this.stickyCtaButton = this.stickyCta?.querySelector('[data-sticky-button]');
    }

    _renderFrequencyLabels() {
      this.frequencyRadios.forEach((radio, i) => {
        const plan = this.plans[i];
        if (!plan) return;
        const meta = this._getPlanMeta(plan);
        const label = this.frequencyLabels[i];
        const badge = this.frequencyBadges[i];

        // Set label based on variant (skip in bundle mode to keep Recharge plan names)
        if (label && !this.bundleMode) {
          label.textContent = this.variant === 'alt' ? meta.altLabel : meta.bannerLabel;
        }

        // Set badge: only the plan matching popularPlanTarget gets "Most Popular"
        if (badge) {
          const planKey = this._getPlanKey(plan);
          if (planKey === this.popularPlanTarget) {
            badge.textContent = 'Most Popular';
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        }

        // Update sub-text with per-box price (both variants)
        const sub = radio.closest('.sprouties-frequency__pill')?.querySelector('.sprouties-frequency__pill-sub');
        if (sub) {
          const pricing = this._computePlanPricing(plan);
          sub.textContent = `${this._formatMoney(pricing.perBoxCents)} per box`;
        }
      });
    }

    _bindEvents() {
      // Purchase type toggle
      if (this.subscribeRadio) {
        this.subscribeRadio.addEventListener('change', () => {
          this.purchaseType = 'subscribe';
          this._updateUI();
        });
      }
      if (this.onetimeRadio) {
        this.onetimeRadio.addEventListener('change', () => {
          this.purchaseType = 'onetime';
          this._updateUI();
        });
      }

      // Frequency pills
      this.frequencyRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.selectedPlanIndex = parseInt(radio.dataset.planIndex, 10);
          this._updateUI();
        });
      });

      // Alt breakdown toggle
      if (this.breakdownToggle) {
        this.breakdownToggle.addEventListener('click', () => {
          const isOpen = this.breakdownPanel?.classList.toggle('is-open');
          this.breakdownChevron?.classList.toggle('is-open', isOpen);
        });
      }

      // Subscribe nudge (switches back to subscribe)
      if (this.subscribeNudge) {
        this.subscribeNudge.addEventListener('click', () => {
          if (this.subscribeRadio) {
            this.subscribeRadio.checked = true;
            this.purchaseType = 'subscribe';
            this._updateUI();
          }
        });
      }

      // CTA button states
      if (this.ctaButton) {
        this.ctaButton.addEventListener('click', (e) => {
          // Don't interfere with form submission; just handle visual feedback
          this._handleCtaClick(e);
        });
      }
    }

    _updateUI() {
      const isSubscribe = this.purchaseType === 'subscribe';
      const plan = this.plans[this.selectedPlanIndex];

      // 1. Update selling plan hidden input
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = isSubscribe && plan ? plan.id : '';
      }

      // 2. Update price display (per-box price for subscribe, one-time for buy once)
      if (plan && isSubscribe) {
        const pricing = this._computePlanPricing(plan);

        if (this.priceDisplay) {
          this.priceDisplay.textContent = this._formatMoney(pricing.perBoxCents);
        }
        if (this.comparePriceDisplay) {
          this.comparePriceDisplay.textContent = this._formatMoney(this.oneTimePrice);
          this.comparePriceDisplay.style.display = pricing.boxSavingsCents > 0 ? '' : 'none';
        }
      } else {
        if (this.priceDisplay) {
          this.priceDisplay.textContent = this._formatMoney(this.oneTimePrice);
        }
        if (this.comparePriceDisplay) {
          this.comparePriceDisplay.style.display = 'none';
        }
      }

      // 3. Collapse/expand subscribe card interior (measured height for smooth motion)
      if (this.subscribeExpandable) {
        if (!isSubscribe) {
          // Collapse: set current height first for smooth transition, then collapse
          this.subscribeExpandable.style.maxHeight = this.subscribeExpandable.scrollHeight + 'px';
          // Force reflow to register the starting height
          this.subscribeExpandable.offsetHeight; // eslint-disable-line no-unused-expressions
          this.subscribeExpandable.classList.add('is-collapsed');
        } else {
          // Expand: remove collapsed, set measured height, then clear inline style
          this.subscribeExpandable.classList.remove('is-collapsed');
          this.subscribeExpandable.style.maxHeight = this.subscribeExpandable.scrollHeight + 'px';
          // After transition completes, remove inline max-height so content can reflow
          const onEnd = () => {
            this.subscribeExpandable.style.maxHeight = '';
            this.subscribeExpandable.removeEventListener('transitionend', onEnd);
          };
          this.subscribeExpandable.addEventListener('transitionend', onEnd);
        }
      }

      // 4. Update savings subtitle (per-box savings + delivery)
      if (this.savingsSubtitle && plan && isSubscribe) {
        const pricing = this._computePlanPricing(plan);
        if (pricing.boxSavingsCents > 0) {
          this.savingsSubtitle.textContent = `Save ${this._formatMoney(pricing.boxSavingsCents)} per box + free delivery`;
        } else {
          this.savingsSubtitle.textContent = 'Free delivery on every order';
        }
      }

      // 5. Update banner strip (below frequency pills)
      if (this.bannerStrip && plan && isSubscribe) {
        const pricing = this._computePlanPricing(plan);
        if (this.bannerStripText) {
          if (pricing.boxSavingsCents > 0) {
            this.bannerStripText.textContent = `Save ${this._formatMoney(pricing.boxSavingsCents)}/box on product + free delivery`;
          } else {
            this.bannerStripText.textContent = 'Free delivery on every order';
          }
        }
        this.bannerStrip.style.display = '';
      } else if (this.bannerStrip) {
        this.bannerStrip.style.display = 'none';
      }
      // Legacy banner value (hide if still present in DOM)
      if (this.bannerValue) {
        this.bannerValue.style.display = 'none';
      }

      // 6. Update monthly info + subscriber count (Banner)
      if (this.monthlyInfo && plan && isSubscribe) {
        const pricing = this._computePlanPricing(plan);
        this.monthlyInfo.innerHTML = `${pricing.deliveriesPerMonth} deliveries/mo &middot; <strong>${this._formatMoney(pricing.monthlyPriceCents)}/mo</strong>`;
      }
      if (this.subscriberLine && isSubscribe) {
        const deliveryDate = this._getNextDeliveryDate();
        this.subscriberLine.innerHTML = `Join <strong>${this.subscriberCount}+</strong> subscribers &middot; First delivery: <strong>${deliveryDate}</strong>`;
      }

      // 7. Update CTA label (respects customizer override for subscribe state)
      if (this.ctaLabel) {
        if (isSubscribe && plan) {
          if (this.subscribeCtaOverride) {
            // Customizer override — support {{price}} token
            const pricing = this._computePlanPricing(plan);
            this.ctaLabel.textContent = this.subscribeCtaOverride
              .replace(/\{\{price\}\}/gi, this._formatMoney(pricing.monthlyPriceCents));
          } else {
            // Default dynamic label
            const pricing = this._computePlanPricing(plan);
            if (this.variant === 'alt') {
              this.ctaLabel.textContent = pricing.totalMonthlySavingsCents > 0
                ? `Subscribe & Save \u00b7 ${this._formatMoney(pricing.totalMonthlySavingsCents)}/mo`
                : 'Subscribe & Save';
            } else {
              this.ctaLabel.textContent = `Subscribe \u00b7 ${this._formatMoney(pricing.monthlyPriceCents)}/mo`;
            }
          }
        } else {
          this.ctaLabel.textContent = `Add to Cart \u00b7 ${this._formatMoney(this.oneTimePrice)}`;
        }
      }

      // 8. Subscribe nudge visibility
      if (this.subscribeNudge) {
        this.subscribeNudge.classList.toggle('is-visible', !isSubscribe);
      }

      // 9. Alt variant updates
      if (this.variant === 'alt' && plan && isSubscribe) {
        this._updateAltUI(plan);
      }

      // 10. Sticky CTA sync
      this._updateStickyCta(isSubscribe, plan);

      // 11. Dispatch custom event for other modules to react
      this.container.dispatchEvent(new CustomEvent('sprouties:subscription-change', {
        bubbles: true,
        detail: {
          purchaseType: this.purchaseType,
          planId: isSubscribe && plan ? plan.id : null,
          price: isSubscribe && plan ? plan.price : this.oneTimePrice,
          planIndex: this.selectedPlanIndex
        }
      }));
    }

    _updateAltUI(plan) {
      const pricing = this._computePlanPricing(plan);

      // Summary line
      if (this.summaryMonthly) {
        this.summaryMonthly.textContent = `${this._formatMoney(pricing.monthlyPriceCents)}/mo`;
      }
      if (this.summaryDelivery) {
        this.summaryDelivery.textContent = `First delivery ${this._getNextDeliveryDate()}`;
      }

      // Breakdown detail (all values clamped ≥ 0 by _computePlanPricing)
      if (this.breakdownBoxSavings) {
        this.breakdownBoxSavings.textContent = `AED ${(pricing.monthlyProductSavingsCents / 100).toFixed(0)}`;
      }
      if (this.breakdownDeliverySavings) {
        this.breakdownDeliverySavings.textContent = `AED ${(pricing.monthlyDeliverySavingsCents / 100).toFixed(0)}`;
      }
      if (this.breakdownTotal) {
        this.breakdownTotal.textContent = `AED ${(pricing.totalMonthlySavingsCents / 100).toFixed(0)}/mo`;
      }
    }

    _updateStickyCta(isSubscribe, plan) {
      if (!this.stickyCta) return;

      if (this.stickyCtaPrice) {
        if (isSubscribe && plan) {
          this.stickyCtaPrice.textContent = `${this._formatMoney(plan.price)}/mo`;
        } else {
          this.stickyCtaPrice.textContent = this._formatMoney(this.oneTimePrice);
        }
      }
      if (this.stickyCtaButton) {
        this.stickyCtaButton.textContent = isSubscribe ? 'Subscribe' : 'Add to Cart';
      }
    }

    _handleCtaClick(e) {
      const btn = this.ctaButton;
      if (!btn || btn.classList.contains('is-loading')) return;

      btn.classList.add('is-loading');
      const label = this.ctaLabel;
      const originalText = label?.textContent;

      if (label) {
        label.innerHTML = '<span class="sprouties-cta__spinner"></span> Adding...';
      }

      // Listen for real add-to-cart event from ProductForm (fires on actual cart success)
      const productForm = this.container.closest('product-form');
      const onSuccess = () => {
        cleanup();
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        if (label) {
          label.textContent = 'Added to Cart ✓';
        }
        if (this.purchaseType === 'subscribe') {
          // Subscription: redirect to cart after brief confirmation
          setTimeout(() => { window.location.href = '/cart'; }, 800);
        } else {
          // One-time: stay on page, reset button
          setTimeout(() => {
            btn.classList.remove('is-success');
            if (label) label.textContent = originalText;
          }, 1500);
        }
      };

      // Safety timeout: if no cart event within 10s, reset loading state (no fake success)
      const safetyTimer = setTimeout(() => {
        cleanup();
        btn.classList.remove('is-loading');
        if (label) label.textContent = originalText;
      }, 10000);

      const cleanup = () => {
        if (productForm) productForm.removeEventListener('add-to-cart', onSuccess);
        clearTimeout(safetyTimer);
      };

      if (productForm) {
        productForm.addEventListener('add-to-cart', onSuccess, { once: true });
      }
    }

    _getPlanKey(plan) {
      // Returns normalized key: 'biweekly', '10day', or 'weekly'
      const name = (plan.name || '').toLowerCase();
      if (name.includes('10') || name.includes('rotation')) return '10day';
      if (name.includes('week') && !name.includes('bi') && !name.includes('2')) return 'weekly';
      return 'biweekly';
    }

    _getPlanMeta(plan) {
      // Match plan to our metadata by examining plan name
      return PLAN_META[this._getPlanKey(plan)];
    }

    /**
     * Centralized pricing engine.
     * Treats plan.price as monthly charge cents (from Appstle selling_plan_allocations).
     * Derives per-box, savings, and monthly totals. Clamps savings ≥ 0.
     *
     * @param {Object} plan - Plan object from JSON blob
     * @returns {Object} Computed pricing fields (all in cents unless noted)
     */
    _computePlanPricing(plan) {
      const meta = this._getPlanMeta(plan);
      const monthlyPriceCents = plan.price; // allocation.price IS the monthly charge
      const deliveriesPerMonth = plan.deliveriesPerMonth || meta.deliveriesPerMonth;
      const perBoxCents = Math.round(monthlyPriceCents / deliveriesPerMonth);
      const boxSavingsCents = Math.max(0, this.oneTimePrice - perBoxCents);
      const monthlyProductSavingsCents = boxSavingsCents * deliveriesPerMonth;
      const monthlyDeliverySavingsCents = this.includeDeliverySavings
        ? Math.round(this.deliverySavingAed * 100) * deliveriesPerMonth
        : 0;
      const totalMonthlySavingsCents = monthlyProductSavingsCents + monthlyDeliverySavingsCents;
      const fullPriceMonthlyCents = this.oneTimePrice * deliveriesPerMonth;

      return {
        monthlyPriceCents,
        deliveriesPerMonth,
        perBoxCents,
        boxSavingsCents,
        monthlyProductSavingsCents,
        monthlyDeliverySavingsCents,
        totalMonthlySavingsCents,
        fullPriceMonthlyCents,
        meta
      };
    }

    _calcTotalMonthlySavings(plan, _meta) {
      // Redirects to centralized pricing engine (kept for backward compat)
      return this._computePlanPricing(plan).totalMonthlySavingsCents / 100;
    }

    _getNextDeliveryDate() {
      const info = getNextDeliveryInfo();
      const d = info.deliveryDate;
      if (info.isToday) return 'today';
      return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
    }

    _formatMoney(cents) {
      return `AED ${(cents / 100).toFixed(2)}`;
    }
  }

  /* ========================================================================
     2. GALLERY
     Mobile: swipe-based with dot indicators
     Desktop: thumbnail click navigation with zoom
     ======================================================================== */

  class SproutiesGallery {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.slider = this.container.querySelector('[data-gallery-slider]');
      this.slides = this.container.querySelectorAll('[data-gallery-slide]');
      this.dots = this.container.querySelectorAll('[data-gallery-dot]');
      this.thumbs = this.container.querySelectorAll('[data-gallery-thumb]');
      this.currentIndex = 0;

      if (this.slides.length === 0) return;

      this._bindEvents();
      this._initDesktopZoom();
      this._initVideoPlayButtons();
    }

    _bindEvents() {
      // Mobile: scroll-based navigation (snap handles position)
      if (this.slider) {
        let scrollTimeout;
        this.slider.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => this._onScroll(), 50);
        }, { passive: true });
      }

      // Dot clicks
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._goTo(i));
      });

      // Thumbnail clicks (desktop)
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this._goTo(i));
      });
    }

    _onScroll() {
      if (!this.slider || !this.slides.length) return;
      const slideWidth = this.slides[0].offsetWidth;
      const gap = parseInt(getComputedStyle(this.slider).gap) || 12;
      const index = Math.round(this.slider.scrollLeft / (slideWidth + gap));
      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this._updateIndicators();
      }
    }

    _goTo(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.currentIndex = index;

      // Mobile: scroll to slide
      if (this.slider && window.innerWidth < 990) {
        const slide = this.slides[index];
        this.slider.scrollTo({
          left: slide.offsetLeft - this.slider.offsetLeft,
          behavior: 'smooth'
        });
      }

      // Desktop: show/hide slides
      if (window.innerWidth >= 990) {
        this.slides.forEach((s, i) => {
          s.classList.toggle('is-active', i === index);
        });
      }

      this._updateIndicators();
    }

    _updateIndicators() {
      // Dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });
      // Thumbs
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    _initVideoPlayButtons() {
      var btns = this.container.querySelectorAll('.sprouties-gallery__play-btn');
      btns.forEach(function(btn) {
        var wrap = btn.closest('.sprouties-gallery__video-wrap');
        if (!wrap) return;
        var video = wrap.querySelector('video');
        if (!video) return;

        btn.addEventListener('click', function() {
          video.play().catch(function() {});
          btn.style.display = 'none';
        });

        video.addEventListener('pause', function() {
          btn.style.display = '';
        });

        video.addEventListener('ended', function() {
          btn.style.display = '';
        });
      });
    }

    _initDesktopZoom() {
      if (window.innerWidth < 990) return;

      this.slides.forEach((slide) => {
        if (!slide.hasAttribute('data-zoom')) return;
        const img = slide.querySelector('img');
        if (!img) return;

        slide.addEventListener('mousemove', (e) => {
          const rect = slide.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${x}% ${y}%`;
        });

        slide.addEventListener('mouseleave', () => {
          img.style.transformOrigin = 'center center';
          img.style.transform = '';
        });
      });
    }
  }

  /* ========================================================================
     3. STICKY CTA (Mobile Only)
     Uses IntersectionObserver on the purchase panel
     ======================================================================== */

  class SproutiesStickyCTA {
    constructor() {
      this.stickyBar = document.querySelector('[data-sprouties-sticky-cta]');
      this.purchasePanel = document.querySelector('[data-sprouties-purchase-panel]');
      this.scrollTarget = document.querySelector('[data-sprouties-scroll-target]');

      if (!this.stickyBar || !this.purchasePanel) return;

      this._initObserver();
      this._bindEvents();
    }

    _initObserver() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const show = !entry.isIntersecting;
            this.stickyBar.classList.toggle('is-visible', show);
            document.body.classList.toggle('has-sprouties-sticky-cta', show);
          });
        },
        { threshold: 0, rootMargin: '-60px 0px 0px 0px' } // Account for header
      );

      observer.observe(this.purchasePanel);
    }

    _bindEvents() {
      // Sticky CTA button scrolls to purchase panel
      const button = this.stickyBar.querySelector('[data-sticky-button]');
      if (button && this.scrollTarget) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
  }

  /* ========================================================================
     4. SHIPPING CUTOFF COUNTDOWN
     8:30am UAE time (GMT+4), updates every 60 seconds
     Delivers every day except Sunday
     ======================================================================== */

  class SproutiesCountdown {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.timeDisplay = this.container.querySelector('[data-countdown-time]');
      this.messageDisplay = this.container.querySelector('[data-countdown-message]');

      this._update();
      this._interval = setInterval(() => this._update(), 60000); // Every minute
    }

    _formatDeliveryDate(d) {
      return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
    }

    _update() {
      const info = getNextDeliveryInfo();
      const suffix = this.container.querySelector('[data-countdown-suffix]');

      if (info.beforeCutoff && info.isToday) {
        // Mon-Sat before 8:30am: countdown to today's delivery
        const hours = Math.floor(info.remainingMin / 60);
        const mins = info.remainingMin % 60;

        if (this.timeDisplay) {
          this.timeDisplay.textContent = `${hours}h ${mins}m`;
        }
        if (this.messageDisplay) {
          this.messageDisplay.textContent = 'Order in the next';
        }
        if (suffix) {
          suffix.textContent = "for today's delivery";
        }
      } else {
        // After cutoff, Saturday afternoon, or Sunday: static next delivery date
        if (this.messageDisplay) {
          this.messageDisplay.textContent = `Next delivery ${this._formatDeliveryDate(info.deliveryDate)}`;
        }
        if (this.timeDisplay) {
          this.timeDisplay.textContent = '';
        }
        if (suffix) suffix.textContent = '';
      }
    }

    destroy() {
      if (this._interval) clearInterval(this._interval);
    }
  }

  /* ========================================================================
     5. FAQ ACCORDION
     Single-open behavior
     ======================================================================== */

  class SproutiesFAQ {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.items = this.container.querySelectorAll('[data-faq-item]');
      this._bindEvents();
    }

    _bindEvents() {
      this.items.forEach((item) => {
        const button = item.querySelector('[data-faq-toggle]');
        if (!button) return;

        button.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');

          // Close all items first (single-open behavior)
          this.items.forEach((other) => {
            if (other !== item) {
              this._close(other);
            }
          });

          // Toggle clicked item
          if (isOpen) {
            this._close(item);
          } else {
            this._open(item);
          }
        });
      });
    }

    _open(item) {
      const answer = item.querySelector('[data-faq-answer]');
      if (!answer) return;

      item.classList.add('is-open');
      // Measure content height for smooth animation
      const inner = answer.querySelector('.sprouties-faq__answer-inner');
      if (inner) {
        answer.style.maxHeight = inner.scrollHeight + 'px';
      }

      // Accessibility
      const button = item.querySelector('[data-faq-toggle]');
      if (button) button.setAttribute('aria-expanded', 'true');
    }

    _close(item) {
      const answer = item.querySelector('[data-faq-answer]');
      if (!answer) return;

      item.classList.remove('is-open');
      answer.style.maxHeight = '0';

      const button = item.querySelector('[data-faq-toggle]');
      if (button) button.setAttribute('aria-expanded', 'false');
    }
  }

  /* ========================================================================
     6. SECTION REVEAL (Scroll Animation)
     IntersectionObserver on below-fold sections
     ======================================================================== */

  class SproutiesReveal {
    constructor() {
      this.sections = document.querySelectorAll('[data-sprouties-reveal]');
      if (!this.sections.length) return;

      // Skip if reduced motion preferred
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.sections.forEach(s => s.classList.add('is-revealed'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );

      this.sections.forEach((section, index) => {
        section.style.transitionDelay = `${index * 50}ms`;
        observer.observe(section);
      });
    }
  }

  /* ========================================================================
     7. REVIEW TICKER — Touch Pause
     CSS animation handles the scroll; JS adds touch pause for mobile
     ======================================================================== */

  class SproutiesTicker {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.track = this.container.querySelector('[data-ticker-track]');
      if (!this.track) return;

      // Touch events for mobile pause
      this.track.addEventListener('touchstart', () => {
        this.track.style.animationPlayState = 'paused';
      }, { passive: true });

      this.track.addEventListener('touchend', () => {
        this.track.style.animationPlayState = 'running';
      }, { passive: true });
    }
  }

  /* ========================================================================
     INITIALIZATION
     Runs after DOM is ready (script is deferred)
     ======================================================================== */

  function init() {
    // Subscription widget(s) — querySelectorAll for multi-instance support
    document.querySelectorAll('[data-sprouties-subscription]').forEach((subWidget) => {
      if (!subWidget.hasAttribute('data-sprouties-initialized')) {
        new SproutiesSubscription(subWidget);
        subWidget.setAttribute('data-sprouties-initialized', '');
      }
    });

    // Gallery/Galleries — querySelectorAll for multi-instance support
    document.querySelectorAll('[data-sprouties-gallery]').forEach((gallery) => {
      if (!gallery.hasAttribute('data-sprouties-initialized')) {
        new SproutiesGallery(gallery);
        gallery.setAttribute('data-sprouties-initialized', '');
      }
    });

    // Sticky CTA (global — init once only)
    if (!document._sproutiesStickyInit) {
      new SproutiesStickyCTA();
      document._sproutiesStickyInit = true;
    }

    // Countdown (store instance on element for teardown on section reload)
    document.querySelectorAll('[data-sprouties-countdown]').forEach((el) => {
      if (!el.hasAttribute('data-sprouties-initialized')) {
        el._sproutiesCountdown = new SproutiesCountdown(el);
        el.setAttribute('data-sprouties-initialized', '');
      }
    });

    // FAQ
    document.querySelectorAll('[data-sprouties-faq]').forEach((el) => {
      if (!el.hasAttribute('data-sprouties-initialized')) {
        new SproutiesFAQ(el);
        el.setAttribute('data-sprouties-initialized', '');
      }
    });

    // Section reveal (global — init once only)
    if (!document._sproutiesRevealInit) {
      new SproutiesReveal();
      document._sproutiesRevealInit = true;
    }

    // Review ticker
    document.querySelectorAll('[data-sprouties-ticker]').forEach((el) => {
      if (!el.hasAttribute('data-sprouties-initialized')) {
        new SproutiesTicker(el);
        el.setAttribute('data-sprouties-initialized', '');
      }
    });

    // If in Shopify editor, force-reveal all sections immediately
    if (window.Shopify && window.Shopify.designMode) {
      document.querySelectorAll('[data-sprouties-reveal]').forEach(el => {
        el.classList.add('is-revealed');
      });
    }
  }

  // Run on DOM ready (script is deferred so DOM should be ready)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── Shopify Theme Editor: section reload handler ──
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;

    // 1. Force-reveal any hidden sections inside the reloaded container
    section.querySelectorAll('[data-sprouties-reveal]').forEach(el => {
      el.classList.add('is-revealed');
    });
    if (section.hasAttribute && section.hasAttribute('data-sprouties-reveal')) {
      section.classList.add('is-revealed');
    }

    // 2. Re-init section-local modules (NOT global observers)
    //    Clear initialized flag first so guard allows re-init
    section.querySelectorAll('[data-sprouties-subscription]').forEach((subWidget) => {
      subWidget.removeAttribute('data-sprouties-initialized');
      new SproutiesSubscription(subWidget);
      subWidget.setAttribute('data-sprouties-initialized', '');
    });

    section.querySelectorAll('[data-sprouties-gallery]').forEach((gallery) => {
      gallery.removeAttribute('data-sprouties-initialized');
      new SproutiesGallery(gallery);
      gallery.setAttribute('data-sprouties-initialized', '');
    });

    section.querySelectorAll('[data-sprouties-countdown]').forEach((el) => {
      // Teardown previous instance to clear setInterval before re-init
      if (el._sproutiesCountdown && typeof el._sproutiesCountdown.destroy === 'function') {
        el._sproutiesCountdown.destroy();
      }
      el.removeAttribute('data-sprouties-initialized');
      el._sproutiesCountdown = new SproutiesCountdown(el);
      el.setAttribute('data-sprouties-initialized', '');
    });

    section.querySelectorAll('[data-sprouties-faq]').forEach((el) => {
      el.removeAttribute('data-sprouties-initialized');
      new SproutiesFAQ(el);
      el.setAttribute('data-sprouties-initialized', '');
    });

    section.querySelectorAll('[data-sprouties-ticker]').forEach((el) => {
      el.removeAttribute('data-sprouties-initialized');
      new SproutiesTicker(el);
      el.setAttribute('data-sprouties-initialized', '');
    });
  });

})();
